import * as React from "react";
import { Box } from "@mui/material";

/** Set --vh to fix mobile 100vh quirks */
function useViewportHeightVar() {
  React.useEffect(() => {
    const set = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };
    set();
    window.addEventListener("resize", set);
    window.addEventListener("orientationchange", set);
    return () => {
      window.removeEventListener("resize", set);
      window.removeEventListener("orientationchange", set);
    };
  }, []);
}

export function FullPagePager({
  children,
  initialIndex = 0,
  transitionMs = 800,
  easing = "cubic-bezier(0.22, 1, 0.36, 1)",
  wheelThreshold = 60,
  swipeThreshold = 60,
  showDots = true,
  autoEnterExit = true,

  /** NEW: instant snap tuning */
  enterDepthPx = 0,        // snap as soon as wrapper is visible by at least 1px
  armBufferRatio = 0.03,   // how far outside wrapper before re-arming (⅓ of vh)
}) {
  useViewportHeightVar();

  const pages = React.Children.toArray(children);
  const pageCount = pages.length;

  const [index, setIndex] = React.useState(
    Math.min(Math.max(initialIndex, 0), pageCount - 1)
  );
  const [animating, setAnimating] = React.useState(false);
  const [active, setActive] = React.useState(false);

  // Direction-aware arming to prevent pull-back loops
  const [armedDown, setArmedDown] = React.useState(true);
  const [armedUp, setArmedUp] = React.useState(true);

  const wrapperRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const touchStartRef = React.useRef(null);
  const wheelAccumRef = React.useRef(0);
  const animTimerRef = React.useRef(null);
  const lastScrollYRef = React.useRef(
    typeof window !== "undefined" ? window.scrollY : 0
  );

  /** Reserve exact height in normal flow so native scroll passes through */
  const wrapperHeight = `calc(var(--vh, 1vh) * ${pageCount * 100})`;

  // Lock/unlock body scroll while pager is active
  const lockBody = React.useCallback((lock) => {
    if (lock) {
      document.body.style.overflow = "hidden";
      document.body.style.overscrollBehavior = "none";
    } else {
      document.body.style.overflow = "";
      document.body.style.overscrollBehavior = "";
    }
  }, []);
  React.useEffect(() => { lockBody(active); return () => lockBody(false); }, [active, lockBody]);

  const goTo = React.useCallback((i) => {
    const target = Math.min(Math.max(i, 0), pageCount - 1);
    if (target === index) return;
    setIndex(target);
    setAnimating(true);
    clearTimeout(animTimerRef.current);
    animTimerRef.current = setTimeout(() => {
      setAnimating(false);
      wheelAccumRef.current = 0;
    }, transitionMs + 40);
  }, [index, pageCount, transitionMs]);

  const next = React.useCallback(() => !animating && goTo(index + 1), [animating, goTo, index]);
  const prev = React.useCallback(() => !animating && goTo(index - 1), [animating, goTo, index]);

  const getWrapperBounds = React.useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const scrollY = window.scrollY || window.pageYOffset;
    const top = rect.top + scrollY;
    const bottom = top + el.offsetHeight;
    const vh = window.innerHeight;
    return { top, bottom, vh, scrollY };
  }, []);

  const enterPager = React.useCallback((enterAtEnd = false) => {
    const b = getWrapperBounds();
    if (b) {
      const targetY = enterAtEnd ? b.bottom - b.vh : b.top;
      window.scrollTo({ top: targetY, behavior: "auto" });
    }
    setIndex(enterAtEnd ? pageCount - 1 : 0);
    setActive(true);
    setArmedDown(false);
    setArmedUp(false);
  }, [getWrapperBounds, pageCount]);

  const exitPagerDown = React.useCallback(() => {
    const b = getWrapperBounds();
    if (b) window.scrollTo({ top: b.bottom, behavior: "auto" });
    setActive(false);
    setArmedDown(false);
    setArmedUp(true);
  }, [getWrapperBounds]);

  const exitPagerUp = React.useCallback(() => {
    const b = getWrapperBounds();
    if (b) window.scrollTo({ top: b.top - 1, behavior: "auto" });
    setActive(false);
    setArmedDown(true);
    setArmedUp(false);
  }, [getWrapperBounds]);

  /* -------- Auto enter/exit with immediate snap -------- */
  React.useEffect(() => {
    if (!autoEnterExit) return;

    const onScroll = () => {
      const curr = window.scrollY || 0;
      const dirDown = curr > lastScrollYRef.current;
      lastScrollYRef.current = curr;

      const b = getWrapperBounds();
      if (!b) return;

      // Smaller re-arm buffer to reduce dead space
      const armBuffer = b.vh * armBufferRatio;   // e.g., 33% of viewport
      const enterDepth = Math.max(1, enterDepthPx); // snap as soon as we see ≥1px

      // Re-arm when well outside wrapper
      if (curr <= b.top - armBuffer) {
        setArmedDown(true);  // can enter when coming from above
        setArmedUp(false);
      } else if (curr + b.vh >= b.bottom + armBuffer) {
        setArmedDown(false);
        setArmedUp(true);    // can enter when coming from below
      }

      if (!active) {
        // Coming from above (scrolling down): enter as soon as top edge is inside by enterDepth
        if (dirDown && armedDown && curr >= b.top + enterDepth) {
          enterPager(false);
          return;
        }
        // Coming from below (scrolling up): enter as soon as bottom edge is inside by enterDepth
        const viewportBottom = curr + b.vh;
        if (!dirDown && armedUp && viewportBottom <= b.bottom - enterDepth) {
          enterPager(true);
          return;
        }
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [active, armedDown, armedUp, autoEnterExit, enterPager, getWrapperBounds, armBufferRatio, enterDepthPx]);

  /* -------- Wheel / Trackpad while ACTIVE -------- */
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el || !active) return;

    const onWheel = (e) => {
      e.preventDefault();
      if (animating) return;

      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? window.innerHeight : 1;
      const dy = e.deltaY * unit;
      wheelAccumRef.current += dy;

      if (wheelAccumRef.current > wheelThreshold) {
        if (index === pageCount - 1) { wheelAccumRef.current = 0; exitPagerDown(); return; }
        wheelAccumRef.current = 0; next();
      } else if (wheelAccumRef.current < -wheelThreshold) {
        if (index === 0) { wheelAccumRef.current = 0; exitPagerUp(); return; }
        wheelAccumRef.current = 0; prev();
      }
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [active, animating, index, pageCount, wheelThreshold, next, prev, exitPagerDown, exitPagerUp]);

  /* -------- Touch while ACTIVE -------- */
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el || !active) return;

    const onTouchStart = (e) => (touchStartRef.current = e.touches[0].clientY);
    const onTouchMove = (e) => { if (touchStartRef.current != null) e.preventDefault(); };
    const onTouchEnd = (e) => {
      if (animating) return;
      const start = touchStartRef.current;
      touchStartRef.current = null;
      if (start == null) return;
      const end = e.changedTouches[0].clientY;
      const dy = end - start;

      if (Math.abs(dy) < swipeThreshold) return;

      if (dy > 0) {
        if (index === 0) exitPagerUp(); else prev();
      } else {
        if (index === pageCount - 1) exitPagerDown(); else next();
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [active, animating, index, pageCount, swipeThreshold, next, prev, exitPagerDown, exitPagerUp]);

  /* -------- Keyboard while ACTIVE -------- */
  React.useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (["ArrowDown", "PageDown", " "].includes(e.key)) {
        e.preventDefault();
        if (index === pageCount - 1) exitPagerDown(); else next();
      } else if (["ArrowUp", "PageUp"].includes(e.key)) {
        e.preventDefault();
        if (index === 0) exitPagerUp(); else prev();
      } else if (e.key === "Home") { e.preventDefault(); exitPagerUp(); }
      else if (e.key === "End") { e.preventDefault(); exitPagerDown(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, index, pageCount, next, prev, exitPagerDown, exitPagerUp]);

  // Keep animation alignment on resize/orientation while ACTIVE
  React.useEffect(() => {
    if (!active) return;
    const realign = () => {
      setAnimating(true);
      clearTimeout(animTimerRef.current);
      animTimerRef.current = setTimeout(() => setAnimating(false), transitionMs);
    };
    window.addEventListener("resize", realign);
    window.addEventListener("orientationchange", realign);
    return () => {
      window.removeEventListener("resize", realign);
      window.removeEventListener("orientationchange", realign);
    };
  }, [transitionMs, active]);

  const translate = `translate3d(0, ${-index * 100}vh, 0)`;

  return (
    <Box
      ref={wrapperRef}
      sx={{
        position: "relative",
        width: "100%",
        height: wrapperHeight,
        minHeight: wrapperHeight,
      }}
    >
      {active && (
        <Box
          ref={containerRef}
          sx={{
            position: "fixed",
            inset: 0,
            overflow: "hidden",
            touchAction: "none",
            "& .fullpage-height": {
              height: "calc(var(--vh, 1vh) * 100)",
              minHeight: "calc(var(--vh, 1vh) * 100)",
            },
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: "100%",
              transition: `transform ${transitionMs}ms ${easing}`,
              transform: translate,
              willChange: "transform",
            }}
          >
            {React.Children.map(pages, (child, i) =>
              React.isValidElement(child)
                ? React.cloneElement(child, { isActive: i === index, className: "fullpage-height" })
                : child
            )}
          </Box>

          {showDots && (
            <Box
              sx={{
                position: "fixed",
                right: { xs: 10, md: 24 },
                top: "50%",
                transform: "translateY(-50%)",
                display: "flex",
                flexDirection: "column",
                gap: 1.2,
                zIndex: 10,
              }}
            >
              {pages.map((_, i) => (
                <Box
                  key={i}
                  onClick={() => setIndex(i)}
                  role="button"
                  aria-label={`Go to section ${i + 1}`}
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: i === index ? "white" : "rgba(255,255,255,0.4)",
                    cursor: "pointer",
                    transition: "all 200ms ease",
                    "&:hover": { bgcolor: "white" },
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export function FullPagePage({ children, isActive, className, sx = {} }) {
  return (
    <Box component="section" className={className} sx={{ position: "relative", width: "100%", isolation: "isolate", ...sx }}>
      {typeof children === "function" ? children({ isActive }) : children}
    </Box>
  );
}
